import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { getStripePromise } from "./stripePromiseTest";
import {
  createPaymentIntent,
  confirmAuthorizationCompletion,
} from "./utils/firebase";
import { trackPaymentError, trackPaymentSuccess } from "./utils/analytics";
import { createNonce, secureErrorLog } from "./utils/validation";
import { getQueryVariable, getLiveMode, getPaymentReturnUrl } from "./App";
import { SubmitButton } from "./SubmitButton";
import { ErrorPopup } from "./ErrorPopup";
import { LoadingIndicator } from "./LoadingIndicator";
import { ResetButton } from "./ResetButton";
import { ErrorMessage } from "./ErrorMessage";

const getDisplayTourName = (value) => value || "private tour";
const getDisplayPrice = (value) => (value != null ? `${value} PLN` : "Loading");

const TourPreview = ({ tourImagePath, tourName }) => {
  const [hasImageError, setHasImageError] = useState(false);

  if (!tourImagePath || hasImageError) {
    return (
      <div className="TourPreviewPlaceholder">
        <div className="TourPreviewPlaceholderIcon">GH</div>
        <div className="TourPreviewPlaceholderText">
          <strong>{tourName || "Private tour"}</strong>
          <span>Tour image unavailable</span>
        </div>
      </div>
    );
  }

  return (
    <img
      src={tourImagePath}
      alt={`${tourName} preview`}
      className="TourImage"
      onError={() => setHasImageError(true)}
    />
  );
};

const PaymentHero = ({ nameForTour, priceForTour, tourImagePath }) => (
  <section className="PaymentHeroCard">
    <div className="HeroTopline">
      <span className="HeroEyebrow">Private tour checkout</span>
      <div className="HeroTrustPill">Secure payment</div>
    </div>
    <img className="LogoImg" src="logo_poziom2_b.png" alt="GuideHub logo" />
    <div className="HeroHeadlineBlock">
      <h1 className="PaymentTitle">Complete your booking</h1>
      <p className="PaymentIntro">
        Review the selected tour, pay securely, and open the booking details
        right away.
      </p>
    </div>

    <div className="TourImageContainer">
      <TourPreview
        tourImagePath={tourImagePath}
        tourName={getDisplayTourName(nameForTour)}
      />
    </div>

    <div className="PaymentOverviewCard">
      <div className="PaymentOverviewHeader">
        <div>
          <div className="PaymentOverviewLabel">Selected experience</div>
          <div className="PaymentOverviewTitle">
            {getDisplayTourName(nameForTour)}
          </div>
        </div>
        <div className="PaymentOverviewAmount">
          <span>Total</span>
          <strong>{getDisplayPrice(priceForTour)}</strong>
        </div>
      </div>

      <div className="PaymentSteps">
        <div className="Step">
          <span className="StepNumber">1</span>
          <span>Review booking</span>
        </div>
        <div className="Step">
          <span className="StepNumber">2</span>
          <span>Pay securely</span>
        </div>
        <div className="Step">
          <span className="StepNumber">3</span>
          <span>Open booking</span>
        </div>
      </div>

      <div className="HeroStatRow">
        <div className="HeroStat">
          <span className="HeroStatLabel">Payment methods</span>
          <strong>Card, BLIK, P24</strong>
        </div>
        <div className="HeroStat">
          <span className="HeroStatLabel">Confirmation</span>
          <strong>Available right after payment</strong>
        </div>
      </div>
    </div>

    <div className="PaymentGuarantee PaymentGuarantee--hero">
      <p>Your payment is processed through Stripe with secure checkout.</p>
      <p>The booking remains linked to this payment automatically.</p>
      <p>Open the booking after payment to review status and next steps.</p>
    </div>
  </section>
);

const PaymentSuccess = ({ nameForTour, priceForTour, tourImagePath }) => (
  <div className="PaymentFlowShell">
    <section className="PaymentHeroCard PaymentHeroCard--success">
      <div className="HeroEyebrow">Payment complete</div>
      <div className="ResultTitle" role="alert">
        Your tour is confirmed
      </div>
      <div className="ResultMessage">
        <p>Payment received successfully.</p>
        <p>
          You can now open the booking details and review everything in one
          place.
        </p>
      </div>
      <div className="HeroStatRow">
        <div className="HeroStat">
          <span className="HeroStatLabel">Tour</span>
          <strong>{getDisplayTourName(nameForTour)}</strong>
        </div>
        <div className="HeroStat">
          <span className="HeroStatLabel">Total paid</span>
          <strong>{getDisplayPrice(priceForTour)}</strong>
        </div>
      </div>
    </section>

    <div className="Result Result--success">
      <div className="TourImageContainer">
        <TourPreview
          tourImagePath={tourImagePath}
          tourName={getDisplayTourName(nameForTour)}
        />
      </div>
      <div className="ResultMessage ResultMessage--compact">
        <p>Your guide request stays linked to this payment automatically.</p>
        <p>
          Open the booking to see the latest status, meeting details, and next
          actions.
        </p>
      </div>
      <ResetButton
        onClick={() =>
          window.open(`https://app.guidehub.eu/#/tour/${getQueryVariable("TourId")}`)
        }
        buttonText="Open booking details"
        className="ResetButton--primary"
      />
    </div>
  </div>
);

const PaymentForm = ({
  nonce,
  tourImagePath,
  nameForTour,
  priceForTour,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [finished, setFinished] = useState(false);

  const handleError = useCallback((err) => {
    const sanitizedError = secureErrorLog(err);
    trackPaymentError(err.type || "payment_error", sanitizedError.message);
    setError({ message: sanitizedError.message });
    setProcessing(false);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      handleError({ message: "Stripe has not loaded yet" });
      return;
    }

    setProcessing(true);
    const { error: submitError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: getPaymentReturnUrl(getQueryVariable("TourId")),
      },
      redirect: "if_required",
    });

    if (submitError) {
      handleError(submitError);
      return;
    }

    try {
      await confirmAuthorizationCompletion({
        tourId: getQueryVariable("TourId"),
        paymentIntentId: paymentIntent.id,
        isLiveMode: getLiveMode(),
        nonce,
      });
    } catch (authError) {
      handleError(authError);
      return;
    }

    trackPaymentSuccess(
      paymentIntent.amount / 100,
      paymentIntent.currency,
      paymentIntent.payment_method_types[0] || "unknown",
      {
        tourId: getQueryVariable("TourId"),
      },
    );
    setFinished(true);
    setProcessing(false);
  };

  if (finished) {
    return (
      <PaymentSuccess
        nameForTour={nameForTour}
        priceForTour={priceForTour}
        tourImagePath={tourImagePath}
      />
    );
  }

  return (
    <>
      <ErrorPopup error={error} onClose={() => setError(null)} show={!!error} />
      <div className="PaymentFlowShell">
        <PaymentHero
          nameForTour={nameForTour}
          priceForTour={priceForTour}
          tourImagePath={tourImagePath}
        />

        <form onSubmit={handleSubmit} className="Form">
          <div className="FormCardHeader">
            <div>
              <div className="FormCardEyebrow">Secure payment</div>
              <div className="ResultTitle" role="alert">
                Pay for your {getDisplayTourName(nameForTour)}
              </div>
            </div>
            <div className="FormHeaderAmount">
              <span>Total due</span>
              <strong>{getDisplayPrice(priceForTour)}</strong>
            </div>
          </div>

          <div className="PaymentMiniSummary">
            <div className="PaymentMiniSummaryRow">
              <span>Tour</span>
              <strong>{getDisplayTourName(nameForTour)}</strong>
            </div>
            <div className="PaymentMiniSummaryRow">
              <span>Payment methods</span>
              <strong>Card, BLIK, P24</strong>
            </div>
          </div>

          <fieldset className="FormGroup">
            <PaymentElement data-testid="payment-element" />
          </fieldset>

          {error && <ErrorMessage>{error.message}</ErrorMessage>}

          <SubmitButton
            processing={processing}
            error={error}
            disabled={processing}
          >
            Pay {getDisplayPrice(priceForTour)}
          </SubmitButton>

          <div className="PaymentGuarantee">
            <p>Use the secure Stripe form below to finish the booking.</p>
            <p>
              If your bank asks for authentication, complete it and return here.
            </p>
            <p>
              The booking confirmation appears immediately after a successful
              payment.
            </p>
          </div>
        </form>
      </div>
    </>
  );
};

export const MultiPaymentForm = () => {
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nonce] = useState(createNonce());
  const [tourImagePath, setTourImagePath] = useState(null);
  const [nameForTour, setNameForTour] = useState(null);
  const [priceForTour, setPriceForTour] = useState(null);

  useEffect(() => {
    const initialize = async () => {
      try {
        const tourId = getQueryVariable("TourId");
        const db = (await import("./utils/firebase")).default;
        const { collection, getDocs } = await import("firebase/firestore");
        const querySnapshot = await getDocs(collection(db, "Tours"));

        for (const tourDoc of querySnapshot.docs) {
          if (tourId === tourDoc.id || tourId === tourDoc.data().serialId) {
            setPriceForTour(tourDoc.data().price);
            setNameForTour(tourDoc.data().destination);

            const portfolioSnapshot = await getDocs(collection(db, "Portfolio"));
            for (const portfolioDoc of portfolioSnapshot.docs) {
              const portfolioData = portfolioDoc.data();
              const tourName = portfolioData.Tour || portfolioData.tour;
              if (tourName === tourDoc.data().destination) {
                setTourImagePath(
                  portfolioData.imageUrl || portfolioData.ImageUrl,
                );
                break;
              }
            }
            break;
          }
        }

        const intent = await createPaymentIntent({
          tourId,
          paymentMethodId: "",
          isLiveMode: getLiveMode(),
          paymentMethodTypes: ["blik", "p24", "card"],
          returnUrl: getPaymentReturnUrl(tourId),
        });

        if (intent?.data?.clientSecret) {
          setClientSecret(intent.data.clientSecret);
        }
      } catch (e) {
        console.error("Failed to initialize payment element", e);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [nonce]);

  const options = useMemo(
    () => ({
      clientSecret,
      appearance: { theme: "stripe" },
    }),
    [clientSecret],
  );

  const stripe = getStripePromise(getLiveMode());

  if (loading) {
    return <LoadingIndicator text="Preparing secure checkout..." />;
  }

  if (!clientSecret) {
    return (
      <div className="PaymentStateCard PaymentStateCard--error">
        <h2>We couldn't load the payment page</h2>
        <p>
          Please refresh the page or reopen the payment link from your booking.
        </p>
      </div>
    );
  }

  return (
    <Elements stripe={stripe} options={options}>
      <PaymentForm
        nonce={nonce}
        tourImagePath={tourImagePath}
        nameForTour={nameForTour}
        priceForTour={priceForTour}
      />
    </Elements>
  );
};

export default MultiPaymentForm;
